import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2, FileClock, Users, Upload, LogOut, Bot, Menu, X,
  BarChart3, Inbox, Sparkles, CreditCard, Briefcase, KeyRound, Star, UserRound,
} from "lucide-react";
import { auth, authApi, setCurrentUser } from "../../lib/api";
import { usePermissions } from "../../lib/permissions";
import { TID } from "../../constants/testIds";
import ChangePasswordModal from "./ChangePasswordModal";
import EditNameModal from "./EditNameModal";

// Sidebar item → required hsp:* permission. When the user's Mongo perms
// don't carry the key, the item is hidden AND the corresponding route
// (see App.js `Protected`) renders <AccessDenied />. Facilities has no
// gate: it's the base grant every signed-in user gets.
const NAV_ITEMS = [
  { to: "/",              icon: Building2,  label: "Facilities",    testId: "nav-facilities",    permission: null },
  { to: "/watchlist",     icon: Star,       label: "Watchlist",     testId: "nav-watchlist",     permission: null },
  { to: "/notifications", icon: Inbox,      label: "Notifications", testId: "nav-notifications", permission: "hsp:notifications:read" },
  { to: "/ai-edit",       icon: Sparkles,   label: "AI Edit",       testId: "nav-ai-edit",       permission: "hsp:ai:consult" },
  { to: "/reports",       icon: BarChart3,  label: "Reports",       testId: "nav-reports",       permission: "hsp:reports:read" },
  { to: "/audit",         icon: FileClock,  label: "Audit Log",     testId: "nav-audit",         permission: "hsp:audit:read" },
  { to: "/users",         icon: Users,      label: "Users",         testId: "nav-users",         permission: "hsp:users:read" },
  { to: "/companies",     icon: Briefcase,  label: "Companies",     testId: "nav-companies",     permission: "hsp:companies:read" },
  { to: "/plans",         icon: CreditCard, label: "Plans",         testId: "nav-plans",         permission: "hsp:plans:read" },
  { to: "/import",        icon: Upload,     label: "Import Data",   testId: "nav-import",        permission: "hsp:import:facilities_upsert" },
];

const NavItem = ({ to, icon: Icon, label, testId, onClick }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={testId}
    onClick={onClick}
    className={({ isActive }) =>
      `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
        isActive
          ? "bg-nmi-700 text-white"
          : "text-slate-600 hover:bg-nmi-50 hover:text-nmi-800"
      }`
    }
  >
    <Icon size={18} strokeWidth={2} />
    <span>{label}</span>
  </NavLink>
);

const SidebarContent = ({ user, onNavClick, onLogout, onChangePw, onEditName }) => {
  const { has } = usePermissions();
  const visibleNav = React.useMemo(
    () => NAV_ITEMS.filter((it) => !it.permission || has(it.permission)),
    [has],
  );
  return (
  <>
    <NavLink
      to="/"
      end
      data-testid="sidebar-logo-home"
      onClick={onNavClick}
      className="flex h-16 items-center gap-3 border-b border-slate-200 px-5 hover:bg-nmi-50 focus:outline-none focus:ring-2 focus:ring-nmi-600/40"
    >
      <img
        src="/nationwide-logo.png"
        alt="Nationwide Medical Insurance — back to Facilities"
        className="h-10 w-10 shrink-0 object-contain"
      />
      <div className="leading-tight min-w-0">
        <div className="font-heading text-[13px] font-black uppercase tracking-wider text-nmi-700">
          Nationwide
        </div>
        <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-500">
          HSP Network Manager
        </div>
      </div>
    </NavLink>

    <nav className="flex-1 space-y-1 p-3">
      {visibleNav.map((it) => (
        <NavItem
          key={it.to}
          to={it.to}
          icon={it.icon}
          label={it.label}
          testId={it.testId}
          onClick={onNavClick}
        />
      ))}
    </nav>

    <div className="border-t border-slate-200 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <button
        type="button"
        onClick={onEditName}
        data-testid="profile-edit-name"
        className="mb-2 flex w-full items-center gap-3 rounded-md bg-white px-3 py-2 border border-slate-200 text-left transition-colors hover:bg-nmi-50 hover:border-nmi-300"
        title="Edit your name"
      >
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-nmi-700 text-white text-xs font-bold shrink-0">
            {(user?.display_name || "?").slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-sm font-semibold text-slate-900">
            {user?.display_name}
          </div>
          <div className="truncate text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
            {user?.role?.replace("_", " ")}
          </div>
        </div>
        <UserRound size={13} className="text-slate-400 shrink-0" />
      </button>
      <div className="grid grid-cols-2 gap-1.5">
        <button
          data-testid="change-password-btn"
          onClick={onChangePw}
          className="flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-2 text-[11px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-nmi-700"
        >
          <KeyRound size={12} strokeWidth={2.5} />
          Password
        </button>
        <button
          data-testid={TID.logout}
          onClick={onLogout}
          className="flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-2 text-[11px] font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-red-700"
        >
          <LogOut size={12} strokeWidth={2.5} />
          Sign out
        </button>
      </div>
      {user?.role === "SUPER_ADMIN" && (
        <div className="mt-3 flex items-center gap-1.5 rounded-md bg-slate-100/60 px-2 py-1.5 text-[10px] font-medium text-slate-500">
          <Bot size={12} strokeWidth={2} />
          Telegram bot connected
        </div>
      )}
    </div>
  </>
  );
};

export const AppLayout = ({ children, topbarExtra, mobileTitle }) => {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = auth.me();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [forced, setForced] = useState(false);

  // Force the password change modal if the current user still has the
  // temporary password. Read from /me — we mirror that state into the
  // auth cache when they successfully log in.
  useEffect(() => {
    const mustChange = !!user?.must_change_password;
    const flagged = searchParams.get("forceChangePassword") === "1";
    if (mustChange || flagged) {
      setForced(true);
      setPwOpen(true);
    }
  }, [user?.must_change_password, searchParams]);

  const onLogout = async () => {
    await auth.logout();
    nav("/login", { replace: true });
  };

  const onPwSuccess = async () => {
    // Refresh /me so must_change_password flips to false.
    try {
      const fresh = await authApi.me();
      setCurrentUser(fresh);
    } catch (e) {
      // Non-fatal — the modal already closed and the next request will
      // rehydrate /me. Log so we can spot silent staleness in Sentry.
      console.warn("[layout] post-password-change /me refresh failed:", e);
    }
    setForced(false);
    // Drop the ?forceChangePassword=1 from the URL.
    if (searchParams.get("forceChangePassword")) {
      searchParams.delete("forceChangePassword");
      setSearchParams(searchParams, { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-slate-200 bg-slate-50 md:flex">
        <SidebarContent
          user={user}
          onLogout={onLogout}
          onChangePw={() => { setForced(false); setPwOpen(true); }}
          onEditName={() => setNameOpen(true)}
        />
      </aside>

      {/* Mobile off-canvas sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-slate-50 shadow-2xl animate-slide-in-right" style={{ transform: "translateX(0)" }}>
            <div className="absolute right-2 top-2">
              <button
                onClick={() => setMobileOpen(false)}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-200"
                aria-label="Close menu"
                data-testid="mobile-menu-close"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarContent
              user={user}
              onLogout={() => { setMobileOpen(false); onLogout(); }}
              onNavClick={() => setMobileOpen(false)}
              onChangePw={() => { setMobileOpen(false); setForced(false); setPwOpen(true); }}
              onEditName={() => { setMobileOpen(false); setNameOpen(true); }}
            />
          </aside>
        </div>
      )}

      <div className="md:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-slate-200 bg-white/90 px-3 backdrop-blur-xl md:h-16 md:px-6">
          <button
            data-testid="mobile-menu-open"
            onClick={() => setMobileOpen(true)}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 md:hidden"
            aria-label="Open menu"
          >
            <Menu size={18} strokeWidth={2.5} />
          </button>
          {mobileTitle && (
            <div className="flex-1 truncate font-heading text-base font-bold text-slate-900 md:hidden">
              {mobileTitle}
            </div>
          )}
          <div className="hidden flex-1 items-center gap-4 md:flex">
            {topbarExtra}
          </div>
        </header>
        {topbarExtra && (
          <div className="border-b border-slate-200 bg-white/95 p-3 backdrop-blur-xl md:hidden">
            <div className="flex flex-col gap-3">{topbarExtra}</div>
          </div>
        )}
        <main className="p-4 sm:p-6 md:p-8 pb-[max(1rem,env(safe-area-inset-bottom))]">{children}</main>
        <footer className="border-t border-slate-200 bg-white/60 px-4 py-3 sm:px-6 md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
            <span>© {new Date().getFullYear()} Nationwide Medical Insurance Ghana Ltd.</span>
            <div className="flex items-center gap-3">
              <a href="/terms" data-testid="footer-terms-link" className="hover:text-nmi-700 hover:underline">Terms</a>
              <span className="text-slate-300">·</span>
              <a href="/privacy" data-testid="footer-privacy-link" className="hover:text-nmi-700 hover:underline">Privacy</a>
            </div>
          </div>
        </footer>
      </div>
      <ChangePasswordModal
        open={pwOpen}
        forced={forced}
        onClose={() => setPwOpen(false)}
        onSuccess={onPwSuccess}
      />
      <EditNameModal
        open={nameOpen}
        user={user}
        onClose={() => setNameOpen(false)}
      />
    </div>
  );
};
